import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { kUserFilesFoldersApiBasePath } from '../constants'
import { CreateFolderRequest, FolderResponse, FolderResponseSchema } from '../schemas'
import { createUserFilesApiError, formatUserFilesErrorDetail, isExpectedUserFilesApiError } from '@/user_files/utils/api-error'

/**
 * Create a new folder under a parent folder.
 *
 * @param request - The folder creation request containing parent_id and name
 * @returns FolderResponse containing the newly created folder
 * @throws Error when user is not authenticated
 * @throws ApiError when the create-folder request fails
 */
export async function createFolder(request: CreateFolderRequest): Promise<FolderResponse> {
  const sentryTransactionId = nanoid()

  try {
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesFoldersApiBasePath}/`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Transaction-Id': sentryTransactionId,
      },
      body: JSON.stringify(request),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const error = createUserFilesApiError({ status: response.status, data: errorData, fallbackMessage: `Failed to create folder: ${response.status}` })
      const detail = formatUserFilesErrorDetail(errorData)

      if (!isExpectedUserFilesApiError(error)) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentryTransactionId })
          Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
            extra: {
              ...getResponseDebugContext(response, url),
              requestBody: request,
              responseData: errorData,
              parseError: parseError ? formatUnknownError(parseError) : null,
            },
          })
        })
      }

      throw error
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Create folder response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            requestBody: request,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid create folder response JSON')
    }

    const parsed = FolderResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Create folder response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            requestBody: request,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid create folder response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { requestBody: request },
        })
      })
    }
    throw error
  }
}
